import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams} from 'react-router-dom';

import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container, Box, Typography, CircularProgress, Alert, Grid, Card, CardContent,
    TextField, Button, FormControlLabel, Switch, FormControl, InputLabel, Select,
    MenuItem, InputAdornment, Divider, FormHelperText, AlertTitle, IconButton,
    ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import backendURL from '../../config';

// --- Tiptap Imports ---
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';
import { useEditor, EditorContent } from '@tiptap/react';

// Styled components
const EditorContainer = styled(Box)(({ theme, error }) => ({
    border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    '&:focus-within': {
        borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.25)}`,
    },
}));

const MenuBar = styled(Box)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
}));

const StyledEditorContainer = styled(Box)(({ theme }) => ({
    minHeight: '120px',
    maxHeight: '300px',
    overflowY: 'auto',
    padding: theme.spacing(1.5),
    '& .ProseMirror': {
        outline: 'none',
        border: 'none',
        fontSize: '14px',
        lineHeight: '1.5',
        fontFamily: theme.typography.body1.fontFamily,
        color: theme.palette.text.primary,
        '&.ProseMirror-focused': {
            outline: 'none',
        },
        '& p': {
            margin: '0 0 8px 0',
            '&:last-child': {
                marginBottom: 0,
            },
        },
        '& h1, & h2, & h3': {
            margin: '16px 0 8px 0',
            fontWeight: 600,
            '&:first-child': {
                marginTop: 0,
            },
        },
        '& h1': {
            fontSize: '1.5rem',
        },
        '& h2': {
            fontSize: '1.25rem',
        },
        '& h3': {
            fontSize: '1.125rem',
        },
        '& ul, & ol': {
            margin: '8px 0',
            paddingLeft: '20px',
        },
        '& li': {
            margin: '4px 0',
        },
        '& blockquote': {
            margin: '8px 0',
            paddingLeft: '16px',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
        },
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            '&:hover': {
                textDecoration: 'none',
            },
        },
        '& strong': {
            fontWeight: 600,
        },
        '& em': {
            fontStyle: 'italic',
        },
        '& u': {
            textDecoration: 'underline',
        },
        '& s': {
            textDecoration: 'line-through',
        },
        '& p.is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: theme.palette.text.disabled,
            pointerEvents: 'none',
            height: 0,
        },
    },
}));

// Helper functions
const stripHtml = (html) => {
    if (typeof window === 'undefined' || !html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
};

const ensureUrlProtocol = (url) => {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
        return 'https://' + url;
    }
    return url;
};

// Menu Bar Component
const TiptapMenuBar = ({ editor }) => {
    const [isLinkModalOpen, setLinkModalOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');

    const openLinkModal = useCallback(() => {
        const url = editor.getAttributes('link').href || '';
        setLinkUrl(url);
        setLinkModalOpen(true);
    }, [editor]);

    const closeLinkModal = () => {
        setLinkModalOpen(false);
        setLinkUrl('');
    };

    const saveLink = useCallback(() => {
        if (linkUrl.trim()) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: ensureUrlProtocol(linkUrl.trim()) }).run();
        } else {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        closeLinkModal();
    }, [editor, linkUrl]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveLink();
        }
    };

    if (!editor) return null;

    const getActiveHeadingLevel = () => {
        for (let i = 1; i <= 3; i++) {
            if (editor.isActive('heading', { level: i })) return `h${i}`;
        }
        return 'paragraph';
    };

    const handleHeadingChange = (event) => {
        const value = event.target.value;
        if (value === 'paragraph') {
            editor.chain().focus().setParagraph().run();
        } else {
            const level = parseInt(value.slice(1), 10);
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };

    return (
        <>
            <MenuBar>
                <IconButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} size="small" title="Undo"><UndoIcon /></IconButton>
                <IconButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} size="small" title="Redo"><RedoIcon /></IconButton>
                <Divider orientation="vertical" flexItem />
                <Select size="small" value={getActiveHeadingLevel()} onChange={handleHeadingChange} sx={{ minWidth: 120, '.MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}>
                    <MenuItem value="paragraph">Paragraph</MenuItem>
                    <MenuItem value="h1">Heading 1</MenuItem>
                    <MenuItem value="h2">Heading 2</MenuItem>
                    <MenuItem value="h3">Heading 3</MenuItem>
                </Select>
                <Divider orientation="vertical" flexItem />
                <ToggleButtonGroup size="small" aria-label="text formatting">
                    <ToggleButton value="bold" aria-label="bold" onClick={() => editor.chain().focus().toggleBold().run()} selected={editor.isActive('bold')} title="Bold"><FormatBoldIcon /></ToggleButton>
                    <ToggleButton value="italic" aria-label="italic" onClick={() => editor.chain().focus().toggleItalic().run()} selected={editor.isActive('italic')} title="Italic"><FormatItalicIcon /></ToggleButton>
                    <ToggleButton value="underline" aria-label="underline" onClick={() => editor.chain().focus().toggleUnderline().run()} selected={editor.isActive('underline')} title="Underline"><FormatUnderlinedIcon /></ToggleButton>
                    <ToggleButton value="strike" aria-label="strike" onClick={() => editor.chain().focus().toggleStrike().run()} selected={editor.isActive('strike')} title="Strikethrough"><StrikethroughSIcon /></ToggleButton>
                </ToggleButtonGroup>
                <Divider orientation="vertical" flexItem />
                <ToggleButtonGroup size="small" aria-label="link formatting">
                    <ToggleButton value="link" aria-label="add or edit: 'add or edit link" onClick={openLinkModal} selected={editor.isActive('link')} title="Add/Edit Link"><LinkIcon /></ToggleButton>
                    <ToggleButton value="unlink" aria-label="remove link" onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive('link')} title="Remove Link"><LinkOffIcon /></ToggleButton>
                </ToggleButtonGroup>
                <Divider orientation="vertical" flexItem />
                <ToggleButtonGroup size="small" aria-label="block formatting">
                    <ToggleButton value="bulletList" aria-label="bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} selected={editor.isActive('bulletList')} title="Bullet List"><FormatListBulletedIcon /></ToggleButton>
                    <ToggleButton value="orderedList" aria-label="ordered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} selected={editor.isActive('orderedList')} title="Numbered List"><FormatListNumberedIcon /></ToggleButton>
                    <ToggleButton value="blockquote" aria-label="blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} selected={editor.isActive('blockquote')} title="Quote"><FormatQuoteIcon /></ToggleButton>
                </ToggleButtonGroup>
            </MenuBar>
            <Dialog open={isLinkModalOpen} onClose={closeLinkModal} fullWidth maxWidth="sm">
                <DialogTitle>Edit Link</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" id="link-url" label="URL" type="url" fullWidth variant="outlined" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={handleKeyDown} placeholder="https://example.com" helperText="Enter a valid URL (e.g., https://example.com)" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeLinkModal}>Cancel</Button>
                    <Button onClick={saveLink} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Main Editor Component
const TiptapEditor = ({ content, onContentChange, placeholder = "Start typing...", error = false }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Underline,
            TiptapLink.configure({ openOnClick: false, HTMLAttributes: { class: 'tiptap-link' } })
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onContentChange(html);
        },
    });

    useEffect(() => {
        return () => { if (editor) editor.destroy(); };
    }, [editor]);

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content || '');
        }
    }, [content, editor]);

    if (!editor) {
        return (
            <EditorContainer error={error}>
                <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Loading editor...</Typography></Box>
            </EditorContainer>
        );
    }

    return (
        <EditorContainer error={error}>
            <TiptapMenuBar editor={editor} />
            <StyledEditorContainer><EditorContent editor={editor} /></StyledEditorContainer>
        </EditorContainer>
    );
};

// --- Styled Components (for a Modern & Consistent UI) ---
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.08)',
    transition: 'box-shadow 0.3s ease-in-out', '&:hover': { boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.12)' }
}));
const SectionHeader = styled(Typography)(({ theme }) => ({ marginBottom: theme.spacing(3), fontWeight: 600 }));
const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}` },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
    }
}));
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}` },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
    }
}));
const FileUploadContainer = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.divider}`, borderRadius: '12px', padding: theme.spacing(3),
    textAlign: 'center', cursor: 'pointer', backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: 'background-color 0.3s, border-color 0.3s', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1), borderColor: theme.palette.primary.main }
}));
const ImagePreview = styled('img')({ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '12px', boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.1)' });
const SubmitButton = styled(Button)({ borderRadius: '12px', padding: '10px 24px', fontWeight: '600' });

// --- Validation Logic ---
const validatePublishingForm = (data) => {
    const errors = {};
    if (!data.title || !data.title.trim()) errors.title = 'Chatbot title is required.';
    if (!data.category_key) errors.category_key = 'Profession category is required.';
    if (!data.short_description || !stripHtml(data.short_description).trim()) errors.short_description = 'Short description is required.';
    else if (stripHtml(data.short_description).length > 300) errors.short_description = 'Short description cannot exceed 300 characters.';
    if (!data.long_description || !stripHtml(data.long_description).trim()) errors.long_description = 'Long description is required.';
    return errors;
};

// PublishingForm component
const PublishingForm = ({
    publishingData, metaData, professionOptions,
    handlePublishingChange, handlePublishingTiptapChange, handleCategoryChange, handleFileChange, handlePublishingSubmit,
    thumbnailPreview, isSavingPublishing
}) => {
    const [errors, setErrors] = useState({});
    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validatePublishingForm(publishingData);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length === 0) handlePublishingSubmit(e);
    };
    const shortDescriptionTextLength = useMemo(() => stripHtml(publishingData.short_description || '').length, [publishingData.short_description]);

    return (
        <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <SectionHeader variant="h5">Publishing Details</SectionHeader>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        <Grid item xs={12}><FormControlLabel control={<Switch checked={!!publishingData.is_published} onChange={handlePublishingChange} name="is_published" />} label="Publish Chatbot to Marketplace" /><FormHelperText>Toggle this on to make your chatbot visible to everyone.</FormHelperText></Grid>
                        <Grid item xs={12} md={6}><StyledTextField fullWidth label="Chatbot Title" name="title" value={publishingData.title || ''} onChange={handlePublishingChange} error={!!errors.title} helperText={errors.title || "A catchy title for your chatbot."} required /></Grid>
                        <Grid item xs={12} md={6}>
                            <StyledFormControl fullWidth error={!!errors.category_key} required>
                                <InputLabel id="category-select-label">Profession Category</InputLabel>
                                <Select labelId="category-select-label" name="category_key" value={publishingData.category_key || ''} label="Profession Category" onChange={(e) => handleCategoryChange(e.target.value)}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {professionOptions.map(cat => (<MenuItem key={cat.id} value={cat.value}>{cat.label}</MenuItem>))}
                                </Select>
                                <FormHelperText>{errors.category_key || "This categorizes your chatbot in the marketplace."}</FormHelperText>
                            </StyledFormControl>
                        </Grid>
                        <Grid item xs={12}><Typography variant="subtitle2" component="label" sx={{ mb: 1, display: 'block' }}>Short Description <Typography component="span" color="error">*</Typography></Typography><TiptapEditor content={publishingData.short_description || ''} onContentChange={(newContent) => handlePublishingTiptapChange('short_description', newContent)} placeholder="Enter a brief summary of your chatbot's capabilities..." error={!!errors.short_description} /><FormHelperText error={!!errors.short_description}>{errors.short_description || `A brief summary (max 300 characters). Characters: ${shortDescriptionTextLength}/300`}</FormHelperText></Grid>
                        <Grid item xs={12}><Typography variant="subtitle2" component="label" sx={{ mb: 1, display: 'block' }}>Long Description <Typography component="span" color="error">*</Typography></Typography><TiptapEditor content={publishingData.long_description || ''} onContentChange={(newContent) => handlePublishingTiptapChange('long_description', newContent)} placeholder="Provide detailed information about your chatbot, its features, and how it can help users..." error={!!errors.long_description} /><FormHelperText error={!!errors.long_description}>{errors.long_description || "Detailed description of your chatbot's capabilities."}</FormHelperText></Grid>
                        <Grid item xs={12}><Typography variant="subtitle1" gutterBottom>Thumbnail Image</Typography><Grid container spacing={3} alignItems="center">{thumbnailPreview && (<Grid item><ImagePreview src={thumbnailPreview} alt="Thumbnail Preview" /></Grid>)}<Grid item xs><FileUploadContainer component="label" htmlFor="thumbnail-upload"><UploadFileIcon sx={{ fontSize: 40, color: 'primary.main' }} /><Typography>Click to upload or drag & drop</Typography><Typography variant="caption" color="text.secondary">PNG, JPG, WEBP (400x400 recommended)</Typography><input id="thumbnail-upload" type="file" hidden onChange={handleFileChange} accept="image/*" /></FileUploadContainer></Grid></Grid></Grid>
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}><SubmitButton type="submit" variant="contained" color="primary" disabled={isSavingPublishing}>{isSavingPublishing ? (<CircularProgress size={24} color="inherit" />) : ('Save Publishing Settings')}</SubmitButton></Grid>
                    </Grid>
                </Box>
            </CardContent>
        </StyledCard>
    );
};

// ### UPDATED: Renamed to SignupDetailsSection and completely refactored ###
const SignupDetailsSection = ({ 
    subscriptionData, 
    handleSignupDetailsChange, 
    handleSignupTiptapChange,
    handleSignupSubmit, 
    isSavingSignup 
}) => (
    <StyledCard>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <SectionHeader variant="h5">Signup Details</SectionHeader>
            <Box component="form" onSubmit={handleSignupSubmit} noValidate>
                <FormControlLabel 
                    control={
                        <Switch 
                            checked={!!subscriptionData.allow_signup} 
                            onChange={(e) => handleSignupDetailsChange(e.target.name, e.target.checked)}
                            name="allow_signup"
                        />
                    } 
                    label="Allow users to sign up for this service" 
                />
                <FormHelperText>
                    Enable this to display pricing and a signup option on your marketplace page.
                </FormHelperText>

                {/* Conditional rendering for the form fields */}
                {subscriptionData.allow_signup && (
                    <Box sx={{ mt: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <StyledTextField
                                    fullWidth
                                    label="Price Per Hour"
                                    name="price_per_hour"
                                    type="number"
                                    value={subscriptionData.price_per_hour ?? ''}
                                    onChange={(e) => handleSignupDetailsChange(e.target.name, e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        inputProps: { min: 0, step: "0.01" }
                                    }}
                                    helperText="Set a rate for hourly services."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StyledTextField
                                    fullWidth
                                    label="Price Per Instance"
                                    name="price_per_instance"
                                    type="number"
                                    value={subscriptionData.price_per_instance ?? ''}
                                    onChange={(e) => handleSignupDetailsChange(e.target.name, e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        inputProps: { min: 0, step: "0.01" }
                                    }}
                                    helperText="Set a rate for one-time tasks."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StyledTextField
                                    fullWidth
                                    label="Price Per Month"
                                    name="price_per_month"
                                    type="number"
                                    value={subscriptionData.price_per_month ?? ''}
                                    onChange={(e) => handleSignupDetailsChange(e.target.name, e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        inputProps: { min: 0, step: "0.01" }
                                    }}
                                    helperText="Set a rate for monthly packages."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" component="label" sx={{ mb: 1, display: 'block' }}>
                                    Service Description
                                </Typography>
                                <TiptapEditor
                                    content={subscriptionData.signup_description || ''}
                                    onContentChange={(newContent) => handleSignupTiptapChange('signup_description', newContent)}
                                    placeholder="Describe your services, mention live agent support, specializations, etc."
                                />
                                <FormHelperText sx={{ mt: 1 }}>
                                    Explain what users get when they sign up.
                                </FormHelperText>
                            </Grid>
                        </Grid>
                    </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <SubmitButton 
                        type="submit" 
                        variant="contained" 
                        color="primary" 
                        disabled={isSavingSignup}
                    >
                        {isSavingSignup ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Save Signup Details'
                        )}
                    </SubmitButton>
                </Box>
            </Box>
        </CardContent>
    </StyledCard>
);


// --- Main Page Component ---
const MarketplaceSettings = () => {
    // --- State Management ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userPlanName, setUserPlanName] = useState('');
    const { chatbotId : paramsChatbotId } = useParams();
    const [publishingData, setPublishingData] = useState({
        is_published: false, title: '', short_description: '', long_description: '',
        category_key: '', thumbnail_url: ''
    });
    const [metaData, setMetaData] = useState({ avatars: [] });
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState('');
    const [isSavingPublishing, setIsSavingPublishing] = useState(false);

    // ### UPDATED: State for new signup details structure
    const [subscriptionData, setSubscriptionData] = useState({ 
        allow_signup: false, 
        price_per_hour: 0,
        price_per_instance: 0,
        price_per_month: 0,
        signup_description: ''
    });
    const [isSavingSignup, setIsSavingSignup] = useState(false);
    const [professionOptions, setProfessionOptions] = useState([]);

    // --- Data Fetching ---
    useEffect(() => {
        document.title = "Marketplace Settings | KlonIT";
        const fetchAllData = async () => {
            setLoading(true);
            setError('');
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };

            try {

                const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

                const [settingsResponse, subscriptionResponse] = await Promise.all([
                    axios.get(`${backendURL}/marketplace/settings`, { headers, params }),
                    axios.get(`${backendURL}/subscriptions/current`, { headers , params })
                ]);
                
                if (settingsResponse.data.success) {
                    const responseData = typeof settingsResponse.data.data === 'string' ? JSON.parse(settingsResponse.data.data) : settingsResponse.data.data;
                    const { publishing, subscription, meta } = responseData;
                    
                    const safePublishing = {
                        is_published: false, title: '', short_description: '', long_description: '',
                        category_key: '', thumbnail_url: '', ...publishing
                    };
                    
                    // ### UPDATED: Create safe subscription object with new fields
                    const safeSubscription = {
                        allow_signup: false, price_per_hour: 0, price_per_instance: 0,
                        price_per_month: 0, signup_description: '', ...subscription
                    };
                    
                    setPublishingData(safePublishing);
                    setSubscriptionData(safeSubscription);
                    setMetaData(meta || { avatars: [] });

                    if (safePublishing.thumbnail_url) {
                        const previewUrl = safePublishing.thumbnail_url.startsWith('http') ? safePublishing.thumbnail_url : `${backendURL}/${safePublishing.thumbnail_url}`;
                        setThumbnailPreview(previewUrl);
                    }
                } else {
                    throw new Error(settingsResponse.data.message || 'Failed to fetch settings.');
                }

                if (subscriptionResponse.data.plan) {
                    setUserPlanName(subscriptionResponse.data.plan.name.toLowerCase());
                } else {
                    setUserPlanName('freemium'); 
                }

            } catch (err) {
                console.error("Error fetching marketplace data:", err);
                setError(err.response?.data?.message || err.message || 'An error occurred while fetching settings.');
            } finally {
                setLoading(false);
            }
        };
        const fetchProfessions = async () => {
            try {
                const token = localStorage.getItem("access_token");
                const headers = { Authorization: `Bearer ${token}` };
                const response = await axios.get(`${backendURL}/get-professions`, { headers });
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                if (responseData && responseData.length > 0) {
                    const uniqueProfessions = [...new Map(responseData.map(item => [item.profession_name, item])).values()];
                    setProfessionOptions(uniqueProfessions.map(item => ({ id: item.id, value: item.profession_name.toLowerCase().replace(/\s+/g, '-'), label: item.profession_name })));
                } else { console.log("No professions found."); }
            } catch (err) { console.error("Error fetching professions:", err); }
        };
        fetchAllData();
        fetchProfessions();
    }, []);

    // --- Handlers ---
    const handlePublishingChange = (e) => { const { name, value, type, checked } = e.target; setPublishingData(prev => ({ ...prev, [name]: type === 'checkbox' || type === 'switch' ? checked : value })); };
    const handlePublishingTiptapChange = (fieldName, content) => { setPublishingData(prev => ({ ...prev, [fieldName]: content })); };
    const handleCategoryChange = (categoryKey) => { setPublishingData(prev => ({ ...prev, category_key: categoryKey })); };
    const handleFileChange = (e) => { const file = e.target.files[0]; if (file) { setThumbnailFile(file); setThumbnailPreview(URL.createObjectURL(file)); } };
    
    const handlePublishingSubmit = async (e) => {
        e.preventDefault();
        setIsSavingPublishing(true);
        setError(''); setSuccess('');
        const validationErrors = validatePublishingForm(publishingData);
        if (Object.keys(validationErrors).length > 0) { setError("Please fix the errors before saving."); setIsSavingPublishing(false); return; }
        const data = new FormData();
        Object.keys(publishingData).forEach(key => data.append(key, publishingData[key]));
        if (thumbnailFile) data.append('thumbnail', thumbnailFile);
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/marketplace/publishing`, data, { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
                params });
            if (response.data.success) { setPublishingData(response.data.data); setSuccess('Publishing settings saved successfully!'); window.scrollTo(0, 0); } 
            else { setError(response.data.message || 'An error occurred.'); }
        } catch (err) { setError(err.response?.data?.message || 'Failed to save settings.'); } 
        finally { setIsSavingPublishing(false); }
    };

    // ### UPDATED: Handlers for the new Signup Details section
    const handleSignupDetailsChange = (name, value) => {
        setSubscriptionData(prev => ({ ...prev, [name]: value }));
    };

    const handleSignupTiptapChange = (fieldName, content) => {
        setSubscriptionData(prev => ({ ...prev, [fieldName]: content }));
    };

    const handleSignupSubmit = async (e) => {
        e.preventDefault();
        setIsSavingSignup(true);
        setError('');
        setSuccess('');

        try {

            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

            const token = localStorage.getItem("access_token");
            // The backend endpoint remains the same, but we send the new data structure
            const response = await axios.post(`${backendURL}/marketplace/subscriptions`, subscriptionData, {
                headers: { 'Authorization': `Bearer ${token}` },
                params
            });
            if (response.data.success) {
                setSubscriptionData(response.data.data);
                setSuccess('Signup details saved successfully!');
                window.scrollTo(0, 0);
            } else {
                setError(response.data.message || 'An error occurred.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save settings.');
        } finally {
            setIsSavingSignup(false);
        }
    };
    
    // --- Render Logic ---
    if (loading && !publishingData.title) {
        return (
            <Container><Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box></Container>
        );
    }
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>Marketplace Settings</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Configure how your chatbot appears in the public marketplace and manage how other users can sign up for your services.
            </Typography>

            {success && <Alert severity="success" sx={{ mb: 2 }} action={<IconButton size="small" onClick={() => setSuccess('')}><CloseIcon fontSize="inherit" /></IconButton>}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} action={<IconButton size="small" onClick={() => setError('')}><CloseIcon fontSize="inherit" /></IconButton>}>{error}</Alert>}

            <PublishingForm
                publishingData={publishingData}
                metaData={metaData}
                professionOptions={professionOptions}
                handlePublishingChange={handlePublishingChange}
                handlePublishingTiptapChange={handlePublishingTiptapChange}
                handleCategoryChange={handleCategoryChange}
                handleFileChange={handleFileChange}
                handlePublishingSubmit={handlePublishingSubmit}
                thumbnailPreview={thumbnailPreview}
                isSavingPublishing={isSavingPublishing}
            />

            <Divider sx={{ my: 5, borderColor: 'rgba(0, 0, 0, 0.08)' }} />
            
            {/* ### UPDATED: Render new SignupDetailsSection with new props ### */}
            <SignupDetailsSection
                    subscriptionData={subscriptionData}
                    handleSignupDetailsChange={handleSignupDetailsChange}
                    handleSignupTiptapChange={handleSignupTiptapChange}
                    handleSignupSubmit={handleSignupSubmit}
                    isSavingSignup={isSavingSignup}
                />
        </Container>
    );
};

export default MarketplaceSettings;